/**
 * Batch routine parameter introspection for Oracle schema warming.
 * Reads every routine parameter from `ALL_ARGUMENTS` for the schema and groups
 * them by routine name (and package).
 *
 * S7 item 8 — paged like every other Oracle artefact, keyed by the query's own
 * `ORDER BY (PACKAGE_NAME, OBJECT_NAME, OVERLOAD, SEQUENCE)`, with a
 * `(package, routine, overload)` group carried across a page boundary so one
 * signature never aggregates as two partial ones. On a pre-12c server (no
 * row-limiting clause) it falls back to the single capped query, exactly as
 * `oracleSchemaIntrospection.ts` does — see `oracleSupportsRowLimiting`.
 */

import type { RoutineMetadata, RoutineParameter } from '@/types/electron';
import type { IntrospectionRow } from './introspectionPaging';
import { ROUTINE_ROW_BUDGET } from './introspectionBudgets';
import { pageOracleArtefact, type OraclePagingMode } from './oracleIntrospectionQuery';

function mapRow(row: Record<string, unknown>): RoutineParameter {
  const direction = row.DIRECTION != null ? String(row.DIRECTION).toUpperCase() : 'IN';
  return {
    name: String(row.NAME ?? ''),
    dataType: String(row.DATA_TYPE ?? ''),
    maxLength: row.MAX_LENGTH != null ? Number(row.MAX_LENGTH) : null,
    precision: row.PRECISION_VAL != null ? Number(row.PRECISION_VAL) : null,
    scale: row.SCALE_VAL != null ? Number(row.SCALE_VAL) : null,
    isOutput: direction === 'OUT' || direction === 'IN/OUT',
    charUsed: row.CHAR_USED != null ? String(row.CHAR_USED) : null,
  };
}

export function buildOracleRoutineParamsPageSql(schema: string, pageClause: string): string {
  const escapedSchema = schema.replace(/'/g, "''");
  return `SELECT
      OBJECT_NAME AS "ROUTINE_NAME",
      PACKAGE_NAME AS "PKG_NAME",
      ARGUMENT_NAME AS "NAME",
      DATA_TYPE AS "DATA_TYPE",
      DATA_LENGTH AS "MAX_LENGTH",
      DATA_PRECISION AS "PRECISION_VAL",
      DATA_SCALE AS "SCALE_VAL",
      IN_OUT AS "DIRECTION",
      POSITION AS "POSITION",
      OVERLOAD AS "OVERLOAD",
      CHAR_USED AS "CHAR_USED"
    FROM ALL_ARGUMENTS
    WHERE OWNER = '${escapedSchema}'
      AND POSITION > 0
    ORDER BY PACKAGE_NAME NULLS FIRST, OBJECT_NAME, OVERLOAD NULLS FIRST, SEQUENCE
    ${pageClause}`;
}

/**
 * Pure aggregator — group one page's `ALL_ARGUMENTS` rows into the per-routine
 * metadata map. Exported so the grouping contract is unit-testable without an
 * IPC bridge.
 */
export function aggregateOracleRoutineParamRows(
  rows: readonly IntrospectionRow[],
  schema: string,
): Record<string, RoutineMetadata[]> {
  // Group rows by (package, routine, overload) then build the map
  type GroupKey = string; // "pkg.routine" or "routine"
  const grouped = new Map<GroupKey, Map<string, RoutineParameter[]>>(); // key → overload → params

  for (const row of rows) {
    const routineName = String(row.ROUTINE_NAME ?? '');
    const pkgName = row.PKG_NAME != null ? String(row.PKG_NAME) : null;
    const overloadKey = row.OVERLOAD != null ? String(row.OVERLOAD) : '__none__';
    if (!routineName) continue;

    const mapKey = pkgName ? `${pkgName.toLowerCase()}.${routineName.toLowerCase()}` : routineName.toLowerCase();
    let overloads = grouped.get(mapKey);
    if (!overloads) { overloads = new Map(); grouped.set(mapKey, overloads); }
    let params = overloads.get(overloadKey);
    if (!params) { params = []; overloads.set(overloadKey, params); }
    params.push(mapRow(row));
  }

  const routinesByNameLower: Record<string, RoutineMetadata[]> = {};
  for (const [mapKey, overloads] of grouped) {
    routinesByNameLower[mapKey] = Array.from(overloads.values()).map((parameters) => ({
      schema,
      name: mapKey.includes('.') ? mapKey.split('.').pop()!.toUpperCase() : mapKey.toUpperCase(),
      parameters,
    }));
  }
  return routinesByNameLower;
}

/**
 * Page the schema's routine signatures, merging each page's groups into one
 * map. Deliberately accumulating rather than sink-based: `routinesByNameLower`
 * is replaced wholesale by the cache's merge, and a routine's overloads must
 * be complete before that swap.
 */
export async function fetchOracleRoutineParams(
  connectionId: string,
  schema: string,
  mode?: OraclePagingMode,
): Promise<Record<string, RoutineMetadata[]>> {
  const out: Record<string, RoutineMetadata[]> = {};
  await pageOracleArtefact<Record<string, RoutineMetadata[]>>({
    connectionId,
    schema,
    label: 'routine-params',
    artefact: 'routines',
    totalBudget: ROUTINE_ROW_BUDGET,
    sqlForPage: (pageClause) => buildOracleRoutineParamsPageSql(schema, pageClause),
    aggregate: (rows) => [aggregateOracleRoutineParamRows(rows, schema)],
    sink: (pages) => {
      for (const page of pages) {
        for (const [key, metadata] of Object.entries(page)) {
          (out[key] ??= []).push(...metadata);
        }
      }
    },
    groupKeyOf: (row) =>
      `${String(row.PKG_NAME ?? '')}|${String(row.ROUTINE_NAME ?? '')}|${String(row.OVERLOAD ?? '')}`,
    mode,
  });
  return out;
}
